import React from 'react';
import { Download, FolderOpen, Loader2 } from 'lucide-react';
import type { ToastKind } from '../../components/Toast';
import { Button } from '../../components/ui/Button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/Card';
import { useArchiveExport } from '../directory/useArchiveExport';
import { plural, useLanguage } from '../../lib/i18n';
import { IS_WEB } from '../../lib/platform';

interface RecordingsSectionProps {
  onNotify: (kind: ToastKind, text: string) => void;
}

/**
 * Ayarlar > Kayıtlar. Şimdilik yalnızca arşivi dışa aktarma; kayıt ve ses
 * tercihleri buraya sonra eklenecek.
 */
export const RecordingsSection: React.FC<RecordingsSectionProps> = ({ onNotify }) => {
  const { t } = useLanguage();
  const exporter = useArchiveExport(onNotify);

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t('Arşivi dışa aktar', 'Export your archive')}</CardTitle>
        <CardDescription>
          {IS_WEB
            ? t(
                'Bütün toplantıların ses kaydı, transkripti ve özeti tek bir ZIP dosyası olarak iner. Uygulamadan hiçbir şey silinmez.',
                'The audio, transcript and summary of every meeting download as a single ZIP file. Nothing is deleted from the app.'
              )
            : t(
                'Bütün toplantıların ses kaydı, transkripti ve özeti seçtiğin klasöre indirilir. Uygulamadan hiçbir şey silinmez.',
                'The audio, transcript and summary of every meeting are downloaded to a folder you choose. Nothing is deleted from the app.'
              )}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        <Button variant="outline" disabled={exporter.isRunning} onClick={() => void exporter.run()}>
          {exporter.isRunning ? <Loader2 className="size-4 animate-spin" /> : <Download className="size-4" />}
          {IS_WEB ? t('ZIP olarak indir', 'Download as ZIP') : t('Klasör seç ve aktar', 'Choose a folder and export')}
        </Button>

        {(exporter.isRunning || exporter.result) && (
          <div className="flex flex-wrap items-center gap-3 text-[0.8rem]">
            {exporter.isRunning ? (
              <span className="text-muted-foreground">
                {exporter.progress
                  ? `${exporter.progress.done}/${exporter.progress.total} · ${exporter.progress.current ?? ''}`
                  : IS_WEB
                    ? t('Hazırlanıyor…', 'Preparing…')
                    : t('Klasör seçiliyor…', 'Choosing a folder…')}
              </span>
            ) : (
              <span className="text-emerald-500">
                {t(
                  `${exporter.result!.exported} toplantı aktarıldı.`,
                  `Exported ${plural(exporter.result!.exported, 'meeting', 'meetings')}.`
                )}
              </span>
            )}
            {exporter.result && !IS_WEB && (
              <button
                type="button"
                onClick={exporter.reveal}
                className="inline-flex items-center gap-1 font-medium text-brand hover:underline"
              >
                <FolderOpen className="size-3.5" /> {t('Klasörü aç', 'Open folder')}
              </button>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};
